import { z } from 'zod';
import { UpholsteryDesignKeySchema } from './upholstery-design.schema';
import { PillowDesignKeySchema } from './pillow-design.schema';

/** Per-weight overrides applied when a Banquette item has a specific Upholstery
 *  Design Type. Only the keys present override; missing keys fall through to
 *  the singleton's flat weight columns. `.strict()` rejects unknown weight names
 *  to catch typos in stored JSON. */
export const BqWeightOverrideSchema = z.object({
  weight_bq_section_width: z.number().optional(),
  weight_bq_section_depth: z.number().optional(),
  weight_bq_section_height: z.number().optional(),
  weight_bq_panel_count: z.number().optional(),
  weight_bq_curved_panel_count: z.number().optional(),
  weight_bq_springs: z.number().optional(),
  weight_bq_foam_sqft: z.number().optional(),
  weight_bq_fabric_cutting: z.number().optional(),
  weight_bq_fabric_sewing: z.number().optional(),
  weight_bq_vertical_tufted_channels: z.number().optional(),
  weight_bq_horizontal_tufted_channels: z.number().optional(),
  weight_bq_button_tufts: z.number().optional(),
  weight_bq_qc_presenting: z.number().optional(),
  weight_bq_upholstery_mounting: z.number().optional(),
}).strict();

/** Design key -> weight overrides. Keyed by the slug schema rather than the old
 *  enum: a record with an enum key type validates every key present, so an
 *  admin-created design used to make this PATCH fail with HTTP 400. */
export const BqDesignTypeOverridesSchema = z.record(
  UpholsteryDesignKeySchema,
  BqWeightOverrideSchema,
).default({});

export type BqWeightOverride = z.infer<typeof BqWeightOverrideSchema>;
export type BqDesignTypeOverrides = z.infer<typeof BqDesignTypeOverridesSchema>;

/** Per-weight overrides applied when a Decorative Pillow uses a specific pillow
 *  design. A SEPARATE map from BqDesignTypeOverridesSchema, not an extension of
 *  it: that one is `.strict()` over the weight_bq_* names and keyed by
 *  UpholsteryDesignKeySchema, so a weight_pl_* name or a pillow slug nested
 *  inside it is an HTTP 400 through the controller's ZodValidationPipe. */
export const PlWeightOverrideSchema = z.object({
  weight_pl_fabric_sewing: z.number().optional(),
  weight_pl_prep_time: z.number().optional(),
}).strict();

/** Pillow design key -> weight overrides. Keyed by the pillow slug schema, so
 *  an admin-created pillow design works without a code change. */
export const PlDesignTypeOverridesSchema = z.record(
  PillowDesignKeySchema,
  PlWeightOverrideSchema,
).default({});

export type PlWeightOverride = z.infer<typeof PlWeightOverrideSchema>;
export type PlDesignTypeOverrides = z.infer<typeof PlDesignTypeOverridesSchema>;

export const ComplexityScoringSettingsSchema = z.object({
  id: z.string().uuid(),
  weight_panel_count: z.number().default(5),
  weight_zone_count: z.number().default(15),
  weight_has_joints: z.number().default(30),
  weight_width: z.number().default(0.5),
  weight_has_cubby_grid: z.number().default(45),
  weight_cubby_cell_count: z.number().default(5),
  weight_leaf_zone_count: z.number().default(8),
  weight_sub_zone_count: z.number().default(5),
  weight_cubby_merge_count: z.number().default(10),
  weight_num_doors: z.number().default(15),
  weight_num_drawers: z.number().default(20),
  weight_bq_panel_count: z.number().default(7.5),
  weight_bq_curved_panel_count: z.number().default(7.5),
  weight_bq_section_width: z.number().default(1.0),
  weight_bq_section_depth: z.number().default(1.5),
  weight_bq_section_height: z.number().default(1.0),
  weight_bq_springs: z.number().default(2),
  weight_bq_foam_sqft: z.number().default(3),
  weight_bq_fabric_cutting: z.number().default(0),
  weight_bq_fabric_sewing: z.number().default(0),
  weight_bq_vertical_tufted_channels: z.number().default(0),
  weight_bq_horizontal_tufted_channels: z.number().default(0),
  weight_bq_button_tufts: z.number().default(0),
  weight_bq_qc_presenting: z.number().default(0),
  weight_bq_upholstery_mounting: z.number().default(0),
  // Finishing weights are deliberately NOT in BqWeightOverrideSchema: that
  // override map is keyed by back-cushion upholstery design types, which have
  // nothing to say about wood finishing.
  weight_bq_finish_sqft: z.number().default(0),
  weight_bq_finish_panel: z.number().default(0),
  bq_design_type_overrides: BqDesignTypeOverridesSchema,
  // Decorative pillow. Minutes per square foot of the LITERAL W x H face —
  // NOT the banquette's six-face wrappedSurfaceSqft. Both default 0 so a
  // pillow scores nothing until the shop tunes them.
  weight_pl_fabric_sewing: z.number().default(0),
  weight_pl_prep_time: z.number().default(0),
  pl_design_type_overrides: PlDesignTypeOverridesSchema,
  tier_standard_max: z.number().int().default(60),
  tier_moderate_max: z.number().int().default(180),
  tier_bq_standard_max: z.number().int().default(90),
  tier_bq_moderate_max: z.number().int().default(180),
  // A pillow's minute count is an order of magnitude below a banquette's, so
  // reusing tier_bq_* would file every pillow as standard.
  tier_pl_standard_max: z.number().int().default(30),
  tier_pl_moderate_max: z.number().int().default(60),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const UpdateComplexityScoringSettingsSchema = ComplexityScoringSettingsSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
}).partial();

export type ComplexityScoringSettings = z.infer<typeof ComplexityScoringSettingsSchema>;
export type UpdateComplexityScoringSettings = z.infer<typeof UpdateComplexityScoringSettingsSchema>;
